import React, { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '@whelk/store';
import { trackCustomEvent } from '@whelk/utils';
import { isApiError } from '@whelk/services/src/common';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getIdToken, signInWithPopup } from 'firebase/auth';
import ResponsiveModal from '@whelk/components/src/Base/ResponsiveModal';
import { auth, googleProvider } from '@whelk/utils/src/firebase';
import { FAIL_REASON, LOGIN_RESULT_TYPE } from '@whelk/const/src/login';
import { login, setShowLoginModal } from '@whelk/store/slices/userSlice';
import { LoginResBody, postLoginByFirebase } from '@whelk/services/src/user';
import { GoogleIcon } from '@whelk/assets/src/web/common-icons';
import Image from 'next/image';

import { Toast } from '@whelk/components';
import { assetUrl, privacyPolicyUrl, termsOfServiceUrl } from '@whelk/const';
import { useTheme } from 'next-themes';

const OverseasLoginModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isSigning = useRef(false);
  const [authLoading] = useAuthState(auth);
  const showLoginModal = useSelector((state: RootState) => state.user.showLoginModal);

  const onLoginError = (err: any, isOAuthError?: boolean) => {
    isSigning.current = false;
    let fail_reason = FAIL_REASON.OTHER;
    if (isOAuthError) {
      fail_reason = FAIL_REASON.OAUTH_ERROR;
      Toast.error('Login failed');
    } else if (isApiError(err)) {
      fail_reason = FAIL_REASON.SERVER_ERROR;
      Toast.error(err.message);
    }
    trackCustomEvent({
      id: 'login_result',
      result_type: LOGIN_RESULT_TYPE.FAIL,
      fail_reason,
      page: 'login_popup_page',
      login_type: 'google',
      popup_type: 'popup',
    });
  };

  const onLoginSuccess = (res: LoginResBody) => {
    isSigning.current = false;
    Toast.success('Login success');
    dispatch(login(res));
    dispatch(setShowLoginModal(false));
    trackCustomEvent({
      id: 'login_result',
      result_type: LOGIN_RESULT_TYPE.SUCCESS,
      login_type: 'google',
      page: 'login_popup_page',
      popup_type: 'popup',
    });
  };

  const handleLogin = async (firebaseIdToken: string) => {
    try {
      if (authLoading && isSigning.current) return;
      isSigning.current = true;
      const res = await postLoginByFirebase({ authToken: firebaseIdToken });
      onLoginSuccess(res);
    } catch (error) {
      onLoginError(error);
    }
  };

  const onLoginClick = async () => {
    try {
      if (isSigning.current) return;
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;
      const firebaseIdToken = await getIdToken(user);
      handleLogin(firebaseIdToken);
    } catch (error) {
      console.log('error', error);
      onLoginError(error, true);
    }
  };

  const onClose = () => {
    dispatch(setShowLoginModal(false));
  };

  return (
    <>
      <ResponsiveModal
        open={showLoginModal}
        onClickMask={onClose}
        size='md'
        className='p-0'
        onClose={onClose}
        closeBtn={false}
      >
        <Image
          src={assetUrl + (isDark ? 'login-bg-dark.png' : 'login-bg-light.png')}
          width={550}
          height={181}
          className='hidden h-[181px] w-full object-cover md:block'
          alt={'login background image'}
        />
        <div className='relative flex flex-col items-center justify-between px-[25px] md:px-[32px]'>
          <div className='font-xl text-col_text00 mt-[30px] text-[24px] font-medium'>
            Welcome to MiniMax
          </div>
          <p className='text-col_text02 mt-2 text-[14px] font-[400] leading-[24px]'>
            Create an account or log in to continue
          </p>
          <button
            className='border-col_line01 md:hover:bg-col_text05 bg-col_text00 md:bg-col_bg09 mx-[30px] mb-[25px]  mt-[32px] flex h-[48px] w-full items-center justify-center rounded-full border transition-all duration-300 md:mb-[56px] md:mt-[30px] md:h-[60px] md:px-[14px] md:font-[500]'
            onClick={onLoginClick}
          >
            <span>
              <GoogleIcon />
            </span>
            <span className='font-500] text-col_text05 md:text-col_text00 pl-[10px] text-[15px] leading-[20px] md:text-[16px] '>
              Continue with Google
            </span>
          </button>
          <div className='text-col_text02 mb-[36px] flex flex-wrap items-center justify-center gap-1 text-[12px] md:px-[80px]'>
            <div>By signing in, you are agreeing to the</div>
            <a
              className='text-col_text06 md:hover:text-col_accent00 active:text-col_text06 visited:text-col_text06 '
              href={termsOfServiceUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('login_user_agreement')}
            </a>
            <span> {t('login_read_and_agree_and01')} </span>
            <a
              className='text-col_text06 md:hover:text-col_accent00 active:text-col_text06 visited:text-col_text06 '
              href={privacyPolicyUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('login_privacy_policy')}
            </a>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default OverseasLoginModal;
